import { useEffect, useState } from "react";

export interface IVisualViewportState {
  /** Height of the visible part of the page (shrinks when the on-screen keyboard opens). */
  height: number;
  /** How far the visible part is scrolled down within the layout viewport (iOS pans it to reveal a focused input). */
  offsetTop: number;
  /** True when the visible area is noticeably shorter than the window — i.e. a soft keyboard is up. */
  isKeyboardOpen: boolean;
}

/** Visible-area shrink below which we treat the difference as a soft keyboard, not browser chrome. */
const KEYBOARD_MIN_HEIGHT_PX = 120;

const readState = (): IVisualViewportState => {
  if (typeof window === "undefined") {
    return { height: 0, offsetTop: 0, isKeyboardOpen: false };
  }
  const vv = window.visualViewport;
  const height = vv?.height ?? window.innerHeight;
  const offsetTop = vv?.offsetTop ?? 0;
  return {
    height,
    offsetTop,
    isKeyboardOpen: window.innerHeight - height > KEYBOARD_MIN_HEIGHT_PX,
  };
};

/**
 * Tracks the browser's visual viewport. On iOS Safari the layout viewport (and every
 * `position: fixed` element in it) keeps its full height when the keyboard opens; only
 * the *visual* viewport shrinks and pans, which is why a full-height dialog "jumps up"
 * and leaves a dark band under itself. Consumers use `height`/`offsetTop` to fit
 * themselves inside the area that is actually on screen.
 */
export const useVisualViewport = (): IVisualViewportState => {
  const [state, setState] = useState<IVisualViewportState>(readState);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setState(readState());
    const vv = window.visualViewport;

    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return state;
};
